<template>
  <transition name="modal">
    <div class="modal-overlay" @click.self="close">
      <div class="modal-content taxes-modal" role="dialog" aria-modal="true" :aria-label="$t('taxas.titulo')">
        <h2 class="taxas__titulo">{{ $t('taxas.titulo') }}</h2>

        <div class="taxas__ouro">
          <FishIcon nome="coins" tamanho="medio" />
          <span class="taxas__valor">{{ formattedGold }}</span>
        </div>

        <div class="taxas__recibo">
          <!-- Um lançamento só: o imposto agora é calculado no servidor
               (barco + mapa juntos), e o cliente apenas anima o valor real. -->
          <div class="taxas__lancamento" :class="{ 'taxas__lancamento--visivel': showTax }">
            <span class="taxas__rotulo">
              <FishIcon nome="ship" tamanho="pequeno" />
              {{ $t('taxas.lancamento') }}
            </span>
            <span class="taxas__debito">-{{ formattedTax }}</span>
          </div>

          <div class="taxas__total" :class="{ 'taxas__total--visivel': showTotal }">
            <span>{{ $t('taxas.total') }}</span>
            <span class="taxas__debito">{{ formattedTax }}</span>
          </div>
        </div>

        <FishButton bloco :desabilitado="!animationComplete" som="" @click="close">
          {{ $t('taxas.fechar') }}
        </FishButton>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex';
import { FishButton, FishIcon } from '@/shared/ui';

/**
 * A "Daily Kingdom Taxes" virou a TAXA DO PORTO: recibo de papel, em vez de um painel azul-marinho
 * com título em inglês hardcoded — num jogo que nem tem reino. A lógica não mudou.
 *
 * No Strapi o cliente calculava o imposto (2% do barco + 2% do mapa, com uma
 * tabela MOCK de preços de mapa) e mandava cobrar. No V2 a fórmula mora no
 * servidor: a action `player/chargeTaxes` chama `POST /taxes/charge`, recebe
 * `{ tax, gold }` e já commita SET_GOLD/SET_TAXES_STATUS — este modal só anima
 * o valor que voltou. Cobrar primeiro e animar depois inverte a ordem antiga
 * de propósito: animar um chute e cobrar outro valor era mentir na tela.
 */
export default {
  name: 'TaxesModal',
  components: { FishButton, FishIcon },
  data() {
    return {
      currentGold: 0,
      tax: 0,
      showTax: false,
      showTotal: false,
      animationComplete: false
    };
  },
  computed: {
    ...mapState({
      playerData: state => state.player
    }),
    formattedGold() {
      return new Intl.NumberFormat('pt-BR').format(this.currentGold);
    },
    formattedTax() {
      return new Intl.NumberFormat('pt-BR').format(this.tax);
    }
  },
  mounted() {
    // Uma cobrança por abertura do modal — o watch antigo em playerData
    // redisparava a sequência a cada mutação do estado do jogador.
    this.chargeAndAnimate();
  },
  methods: {
    async chargeAndAnimate() {
      this.currentGold = this.playerData?.gold || 0;

      let resultado;
      try {
        // O servidor calcula, cobra e devolve o valor real; a action já
        // commitou SET_GOLD (saldo final) e SET_TAXES_STATUS.
        resultado = await this.$store.dispatch('player/chargeTaxes');
      } catch (error) {
        // 409 = "Nao ha imposto pendente": nada a cobrar (o flag local estava
        // adiantado) — fecha sem teatro e desarma o aviso de imposto.
        if (error.response?.status === 409) {
          this.$store.commit('player/SET_TAXES_STATUS', false);
          this.animationComplete = true;
          this.$emit('close');
          return;
        }
        console.error(
          'Error charging taxes:',
          error.response?.data?.message || error.response?.data?.error?.message || error.message
        );
        // Falhou de verdade: libera o botão para o jogador não ficar preso.
        this.animationComplete = true;
        return;
      }

      this.tax = resultado.tax;
      await this.animateSequence(resultado.gold);
    },
    async animateSequence(finalGold) {
      await this.wait(1000);
      this.showTax = true;
      await this.animateGoldReduction(this.tax);
      // O saldo final exibido é o do servidor, não a subtração local — se algo
      // mais mexeu no gold no meio do caminho, vence a verdade do backend.
      this.currentGold = finalGold;

      await this.wait(500);
      this.showTotal = true;
      this.animationComplete = true;
    },
    async animateGoldReduction(amount) {
      const steps = 20;
      const reduction = amount / steps;
      const stepDuration = 50;

      for (let i = 0; i < steps; i++) {
        await this.wait(stepDuration);
        this.currentGold = Math.max(
          Math.round(this.currentGold - reduction),
          0
        );
      }
    },
    wait(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    close() {
      if (this.animationComplete) {
        this.$emit('close');
      }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(7, 32, 48, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal, 1000);
  padding: var(--e-3);
}

.taxes-modal {
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-panel.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background: var(--papel);
  background-clip: padding-box;
  image-rendering: pixelated;
  color: var(--tinta);
  padding: var(--e-3);
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-shadow: 0 6px 0 rgba(0, 0, 0, 0.4);
}

.taxas__titulo {
  font-family: var(--fonte-display);
  font-size: var(--f-4);
  line-height: var(--lh-curto);
  margin: 0 0 var(--e-3);
}

.taxas__ouro {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--e-2);
  margin-bottom: var(--e-3);
}

.taxas__valor {
  font-family: var(--fonte-display);
  font-size: var(--f-5);
  font-variant-numeric: tabular-nums;
  color: var(--ouro-escuro);
}

/* O recibo: papel um degrau mais claro, com o lançamento riscado a lápis. */
.taxas__recibo {
  background: var(--papel-luz);
  border: 2px solid var(--papel-sombra);
  padding: var(--e-2) var(--e-3);
  margin-bottom: var(--e-3);
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
}

.taxas__lancamento {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--e-2);
  padding: var(--e-1) 0;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity var(--t-medio) var(--curva-suave), transform var(--t-medio) var(--curva-suave);
}

.taxas__lancamento--visivel {
  opacity: 1;
  transform: translateY(0);
}

.taxas__rotulo {
  display: flex;
  align-items: center;
  gap: var(--e-1);
  color: var(--tinta-fraca);
}

.taxas__debito {
  font-family: var(--fonte-display);
  font-variant-numeric: tabular-nums;
  color: var(--cor-ruim);
}

.taxas__total {
  display: flex;
  justify-content: space-between;
  gap: var(--e-2);
  margin-top: var(--e-1);
  padding-top: var(--e-2);
  border-top: 2px dashed var(--papel-sombra);
  font-family: var(--fonte-display);
  opacity: 0;
  transition: opacity var(--t-medio) var(--curva-suave);
}

.taxas__total--visivel {
  opacity: 1;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity var(--t-rapido) var(--curva-suave), transform var(--t-rapido) var(--curva-suave);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.96);
}
</style>
